import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import DescriptionIcon from "@mui/icons-material/Description";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";

/* ===== Paletas coherentes ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Card contenedora ===== */
function Card({ title, palette, children, sx }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        ...sx,
      }}
    >
      <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  );
}

export default function MaintenanceDetails() {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const navigate = useNavigate();

  // Mock de mantenimiento editable
  const [form, setForm] = useState({
    title: "Cambio de aceite",
    date: "2024-04-15",
    cost: "125,50",
    provider: "Taller López",
    nextDate: "2025-04-15",
    kmAtService: "126000",
    nextKm: "141000",
    notes: "Se cambió aceite y filtro. Próximo en 15.000 km.",
  });
  const [file, setFile] = useState(null);

  const onChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleUpload = () => {
    if (!file) return;
    alert(`Documento subido (demo): ${file.name}`);
    setFile(null);
  };
  const handleSave = () => {
    console.log("Guardando (mock):", form);
    alert("Cambios guardados (demo).");
  };

  return (
    <Box
      sx={{
        m: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Botón volver — mismo estilo que VehicleDetails */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 500,
          alignSelf: "flex-start",
          mb: 1,
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        Volver
      </Button>

      <Header
        title="Detalle de Mantenimiento"
        subtitle="Información completa y documentación"
      />

      {/* Contenido a altura completa */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          columnGap: 2,
          rowGap: { xs: 2, lg: 0 },
          alignItems: "stretch",
          mt: 1,
          gap: { xs: 2, lg: 0 },
        }}
      >
        {/* Izquierda: formulario (Notas ocupa el resto) */}
        <Card
          title="Información"
          palette={palette}
          sx={{ overflow: "visible", height: { xs: "auto", lg: "100%" } }}
        >
          <Box
            sx={{
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: "10px",
              p: 1.5,
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BuildIcon sx={{ color: palette.accent }} />
              <Typography sx={{ color: palette.text, fontWeight: 700 }}>
                Datos del mantenimiento
              </Typography>
            </Stack>

            {/* Grid de campos superiores */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 1,
              }}
            >
              <TextField
                label="Título"
                value={form.title}
                onChange={onChange("title")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "1 / 3" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
              <TextField
                label="Fecha"
                type="date"
                value={form.date}
                onChange={onChange("date")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
              <TextField
                label="Coste (€)"
                value={form.cost}
                onChange={onChange("cost")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
              <TextField
                label="Proveedor"
                value={form.provider}
                onChange={onChange("provider")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "1 / 2" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
              <TextField
                label="Km en el servicio"
                value={form.kmAtService}
                onChange={onChange("kmAtService")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
              <TextField
                label="Próximo a los (km)"
                value={form.nextKm}
                onChange={onChange("nextKm")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: palette.cardBorder,
                  },
                  "& .MuiInputBase-input, & .MuiFormLabel-root": {
                    color: palette.text,
                  },
                }}
              />
            </Box>

            {/* Notas: ocupa el espacio restante */}
            <TextField
              label="Notas"
              value={form.notes}
              onChange={onChange("notes")}
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              sx={{
                flex: 1,
                minHeight: 0,
                "& .MuiOutlinedInput-root": {
                  display: "flex",
                },
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "flex-start",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.cardBorder,
                },
                "& .MuiInputBase-input, & .MuiFormLabel-root": {
                  color: palette.text,
                },
              }}
            />

            {/* Acciones pegadas abajo */}
            <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ mt: "auto" }}>
              <Button
                onClick={() => navigate(-1)}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  color: palette.text,
                  border: `1px solid ${palette.cardBorder}`,
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: palette.btn,
                  "&:hover": { backgroundColor: palette.btnHover },
                }}
              >
                Guardar
              </Button>
            </Stack>
          </Box>
        </Card>

        {/* Derecha: documentación */}
        <Card title="Documentación" palette={palette}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <DescriptionIcon sx={{ color: palette.accent }} />
            <Typography sx={{ color: palette.text }}>
              Adjunta la factura o parte del taller
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              component="label"
              variant="outlined"
              sx={{
                borderColor: palette.cardBorder,
                color: palette.text,
                textTransform: "none",
              }}
            >
              Seleccionar archivo
              <input
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>

            <TextField
              variant="outlined"
              size="small"
              value={file?.name ?? ""}
              placeholder="Ningún archivo seleccionado"
              InputProps={{ readOnly: true }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: palette.cardBorder,
                },
                "& .MuiInputBase-input": { color: palette.text },
              }}
            />

            <Button
              variant="contained"
              disabled={!file}
              onClick={handleUpload}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Subir
            </Button>
          </Stack>
        </Card>
      </Box>
    </Box>
  );
}
